/**
 * Blocks, newest first, paged back through the record.
 *
 * The ticker beside the standings is the live feed and holds forty rows. This
 * is the history behind it: the same rows, from the database rather than from
 * memory, and pageable to wherever this observer's record begins.
 *
 * The first page sends a cursor too, rather than letting it default. Without
 * one the API answers with the live ticker, which is ordered oldest-first
 * because that is the order it is pushed in — so the first page climbed and
 * every page after it descended. `MAX_SAFE_INTEGER` is "before everything",
 * which is what the front of a descending list means.
 *
 * The gap here is from the chain's own clock, not from when the observer saw
 * each block. `observed_at` is a measurement only at the tip — a gap fill
 * writes a whole batch within the same second — so differencing it down a page
 * of history reports milliseconds between blocks on a chain targeting seconds.
 * The API does that conversion; this only renders it.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import type { RecentBlock } from '../api/generated/RecentBlock'
import { RequestFailed, fetchBlockPage } from '../api/rest'
import { height as fmtHeight, seconds, when } from '../lib/format'
import { href } from '../lib/routes'

export function BlocksIndex({ chain }: { chain: string }) {
  const [rows, setRows] = useState<RecentBlock[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  // The cursor, not the rows: paging replaces the page rather than appending,
  // because a block history is read by position and an endless scroll would
  // make "where am I" unanswerable.
  const [before, setBefore] = useState<number | null>(null)
  // Above any height this or any chain will reach, and the same sentinel the
  // store documents for an absent cursor.
  const FRONT = Number.MAX_SAFE_INTEGER

  useEffect(() => {
    const controller = new AbortController()
    setRows(null)
    setError(null)
    fetchBlockPage(chain, before ?? FRONT, controller.signal)
      .then(setRows)
      .catch((e: unknown) => {
        if (controller.signal.aborted) return
        setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.')
      })
    return () => controller.abort()
  }, [chain, before, FRONT])

  const oldest = rows && rows.length > 0 ? rows[rows.length - 1]!.height : null

  return (
    <section className="panel" style={{ marginBottom: 26 }}>
      <div className="panel-head">
        <h2 className="panel-title">Blocks</h2>
        <span className="eyebrow">
          {rows && rows.length > 0
            ? `#${fmtHeight(rows[0]!.height)} – #${fmtHeight(oldest!)}`
            : 'newest first'}
        </span>
      </div>

      {error && <p className="empty">{error}</p>}
      {!error && rows === null && <p className="empty">Reading the record…</p>}
      {rows !== null && rows.length === 0 && (
        <p className="empty">
          Nothing recorded below this height. The observer's record begins where it started
          watching, not at genesis.
        </p>
      )}

      {rows !== null && rows.length > 0 && (
        <div className="scroll-x">
          <table className="board">
            <caption className="visually-hidden">Blocks, newest first</caption>
            <thead>
              <tr>
                <th scope="col">Block</th>
                <th scope="col" className="left">
                  Miner
                </th>
                <th scope="col">Gap</th>
                <th scope="col" className="event-when">
                  Seen
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.height}>
                  <td className="numeral">
                    <Link to={href({ chain, block: String(b.height) })}>
                      #{fmtHeight(b.height)}
                    </Link>
                  </td>
                  <td className="left">
                    <Link
                      to={href({ chain, miner: b.miner })}
                      className={
                        b.attribution === 'preimage' ? 'miner-name anonymous' : 'miner-name'
                      }
                      title={b.miner}
                    >
                      {b.display}
                    </Link>
                  </td>
                  <td className="numeral">
                    {b.gap_seconds === null ? '—' : `+${seconds(b.gap_seconds)}`}
                  </td>
                  <td className="numeral event-when" style={{ color: 'var(--text-muted)' }}>
                    {when(b.observed_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="pager">
        <button
          className="segmented panel-button"
          disabled={before === null}
          onClick={() => setBefore(null)}
        >
          ← Newest
        </button>
        <button
          className="segmented panel-button"
          disabled={oldest === null || oldest <= 1}
          onClick={() => setBefore(oldest)}
        >
          Older →
        </button>
      </div>

      <p className="panel-note">
        Gaps are the difference between the authors' own timestamps — the chain's clock, not this
        observer's. A block's <em>seen</em> time is when this observer first had it, which for
        anything it caught up on is later than when the chain made it and is not a measurement of
        anything.
      </p>
    </section>
  )
}
